// Impression and click tracking for the owner-only "Find Readers" CTA on the profile work cards
// (Works::FindReadersButtonComponent).
//
// Neither of the page's generic mechanisms reaches this button:
//   - the [data-click-track] binder in profile.wjs.jsx sits inside a guard that skips the
//     profile owner, and this button is shown to nobody else;
//   - watchDomImpressions() scans once on DOMContentLoaded, so it never sees the cards that
//     infinite scroll appends.
// Hence a delegated click listener, and an impression watcher that can be re-run per card.

import {
  ImpressionEntity,
  watchImpressions,
} from 'js/utils/impressions/watchImpressions';
import { trackClick } from 'js/utils/trackClick';
import { viewerIsPremium } from 'js/profiles/autotest/profileFacts';
import {
  hideFindReadersTooltip,
  showFindReadersTooltip,
} from 'js/find_readers/tooltip';
import { readersPageUrl } from 'js/profiles/readersPageUrl';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import routes from 'js/core_pre_webpack/routes';

const BUTTON_SELECTOR = '.js-find-readers-button';
const CTA_SELECTOR = '.js-find-readers-cta';

// Drives the spinner beside the clicked button; see wow/_profile.scss.
const BUSY_CLASS = 'find-readers-cta--busy';

// A run that matched nobody writes no rows, so the readers page has nothing to read back and
// would look identical to a successful send. The outcome is handed forward through
// sessionStorage instead, and FindReadersReport clears it on arrival — one page, once, gone on
// reload. See NO_CANDIDATES_KEY there.
const NO_CANDIDATES_KEY = 'findReaders:noCandidates';

// Private mode and storage-disabled browsers throw on access; losing the message is not worth
// losing the navigation.
const rememberEmptyRun = (): void => {
  try {
    window.sessionStorage.setItem(NO_CANDIDATES_KEY, '1');
  } catch {
    // no message this time
  }
};

// Dispatched by the card's inline script once the view count resolves and the CTA is revealed.
const SHOWN_EVENT = 'find-readers:shown';

// Redshift stores click/impression sources in a varchar(64) and COPY runs TRUNCATECOLUMNS, so an
// over-long name would silently merge with its neighbours. Both are well inside the limit.
//
// These are the PROFILE's, and they are the defaults rather than the only values: this script is
// bound once per page on every surface that draws the button, and each surface names itself on the
// CTA wrapper (see js/find_readers/FindReadersButton). Defaulting to the profile's keeps the Slim
// component — which emits no such attribute — reporting into the series it always has.
const DEFAULT_SOURCE = 'profile-work-strip-find-readers';

// PremiumUpgradeController::FEATURES maps this to the FIND_READERS branch, which renders
// premium_upgrade/find_readers. The trigger distinguishes this door into the feature from any
// later one, and rides through to checkout on the landing page's CTA.
const UPGRADE_FEATURE = 'find_readers';
const DEFAULT_UPGRADE_TRIGGER = 'profile_work_card_find_readers';

const workIdOf = (el: HTMLElement): number | null => {
  const parsed = parseInt(el.dataset.workId ?? '', 10);
  return Number.isNaN(parsed) ? null : parsed;
};

// Both are read off the CTA WRAPPER, which is the one node every surface owns and both callers
// below can reach — the impression watcher is handed it directly, and a click reaches it from the
// button with .closest().
const sourceOf = (cta: HTMLElement | null): string =>
  cta?.dataset.findReadersSource || DEFAULT_SOURCE;

const upgradeTriggerOf = (cta: HTMLElement | null): string =>
  cta?.dataset.findReadersTrigger || DEFAULT_UPGRADE_TRIGGER;

// The marketing page hands `upgrade_redirect` to its checkout CTA, so a member who upgrades lands
// back on the profile they were pitched on rather than the generic post-upgrade page. `work_id`
// rides along to name the paper they clicked, which is what the eventual feature acts on.
//
// `_url`, not `_path`: profiles are served from per-institution subdomains but /upgrade only
// exists on www, so a relative link would 404 on the very page this button lives on.
//
// The redirect is encoded here because the routes helper does not do it — it joins params with a
// bare `${key}=${value}` — so a page name carrying an & or a # would otherwise truncate the
// parameter and silently send the member somewhere else after paying.
export const findReadersUpgradeUrl = (
  workId: number | null,
  trigger: string = DEFAULT_UPGRADE_TRIGGER,
): string =>
  routes.upgrade_url({
    feature: UPGRADE_FEATURE,
    trigger,
    upgrade_redirect: encodeURIComponent(window.location.pathname),
    // Omitted rather than sent as "null": the routes helper stringifies whatever it is given.
    ...(workId == null ? {} : { work_id: workId }),
  });

// Every button on the page, not just the clicked one. A dispatch consumes the sender's whole
// weekly allowance, so a second press on another paper would find itself throttled and quietly do
// nothing — better to show that the allowance is spoken for than to let them spend it twice.
//
// Disabling is also what prevents a double dispatch: a disabled button fires no click event, so
// the delegated listener below never sees the second press.
const setBusy = (cta: HTMLElement | null): void => {
  document
    .querySelectorAll<HTMLButtonElement>(BUTTON_SELECTOR)
    .forEach((button) => {
      button.disabled = true;
    });
  cta?.classList.add(BUSY_CLASS);
};

const clearBusy = (cta: HTMLElement | null): void => {
  document
    .querySelectorAll<HTMLButtonElement>(BUTTON_SELECTOR)
    .forEach((button) => {
      button.disabled = false;
    });
  cta?.classList.remove(BUSY_CLASS);
};

// Read off the DOM rather than held in a module variable, because the DOM is already the source
// of truth — setBusy disables every button — and a flag would be a second copy that can disagree
// with what the page is showing. Browsers fire no click on a disabled control, so this is belt
// and braces; but one dispatch spends the sender's whole weekly allowance, which is worth stating
// as an invariant rather than inheriting from a UI behaviour.
const dispatchInFlight = (): boolean =>
  document.querySelector(`${BUTTON_SELECTOR}:disabled`) != null;

// Runs the premium half of the button: hold the page still, ask the API to find and write to
// readers for this work, then hand the author to the page where those readers will show up.
//
// The endpoint is synchronous and slow by design — it scores an audience, writes a log row per
// recipient and queues the mail before it answers — which is what makes it safe to navigate the
// moment it returns. FindReaders::Dispatch treats "throttled" and "nobody eligible" as success,
// so those arrive here as a 200 and land on the same page; there is nothing for the author to do
// about either, and an error would only invite them to press the button again.
//
// A genuine failure restores the page instead of navigating. It is silent, which is not
// wonderful, but a spinner that never resolves is worse and so is a readers page implying mail
// went out when none did.
const dispatchFindReaders = async (
  button: HTMLElement,
  workId: number,
): Promise<void> => {
  if (dispatchInFlight()) return;

  const cta = button.closest<HTMLElement>(CTA_SELECTOR);
  const destination = readersPageUrl();
  if (destination == null) return;

  setBusy(cta);

  try {
    const response = await fetchWithCsrf(
      routes.api_v0_work_find_readers_path({ work_id: workId }),
      { method: 'POST' },
    );
    if (!response.ok)
      throw new Error(`Find Readers responded ${response.status}`);

    // `throttled` deliberately does not set this: that author has a recent batch, and the box
    // reporting it is a better answer than "we found nobody", which is not what happened.
    const { status } = (await response.json()) as { status?: string };
    if (status === 'no_candidates') rememberEmptyRun();
  } catch {
    clearBusy(cta);
    return;
  }

  window.location.href = destination;
};

// Pressing Back from the readers page does not re-run this page. Browsers keep it in the
// back/forward cache and restore it exactly as it was abandoned: every button disabled, a spinner
// still turning beside the one that was pressed. Nothing left on the restored page will ever
// clear that, because the dispatch it belonged to ended in a navigation.
//
// Reloading rather than just undoing setBusy, because a restored page is stale in the way that
// matters here: the send really happened, so the author's weekly allowance is now spent and the
// button they are looking at is no longer one they can press. Serving them the page the server
// would render today is the only version that tells them the truth.
//
// Conditional on a dispatch actually having been in flight, so backing onto a profile the author
// merely visited is left alone.
const reloadIfRestoredMidDispatch = (event: PageTransitionEvent): void => {
  if (!event.persisted || !dispatchInFlight()) return;

  window.location.reload();
};

// Idempotent, and called from two places. The dataset flag stops us stacking observers on one
// node; watchImpressions additionally dedupes on source:entity_type:entity_id, so even a missed
// flag cannot produce a double count.
const watchCta = (el: HTMLElement): void => {
  if (el.dataset.findReadersWatched === '1') return;

  const workId = workIdOf(el);
  if (workId == null) return;

  el.dataset.findReadersWatched = '1';
  watchImpressions(el, {
    source: sourceOf(el),
    entity_id: workId,
    entity_type: ImpressionEntity.Work,
  });
};

export const setUpFindReaders = (): void => {
  // Delegated on document, so cards appended by infinite scroll need no rebinding.
  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement | null;
    const button = target?.closest<HTMLElement>(BUTTON_SELECTOR);
    if (!button) return;

    const workId = workIdOf(button);
    const cta = button.closest<HTMLElement>(CTA_SELECTOR);
    trackClick({ source: sourceOf(cta), entityId: workId });

    // Tracked either way, so the click-through rate stays comparable across the two audiences.
    //
    // `viewerIsPremium` fails closed — anything short of an explicit true reads as free — so a
    // misread sends someone to the marketing page rather than firing a hundred emails, and it is
    // harmless for an actual member, since PremiumUpgradeController redirects them straight out.
    if (!viewerIsPremium()) {
      window.location.href = findReadersUpgradeUrl(
        workId,
        upgradeTriggerOf(cta),
      );
      return;
    }

    // Nothing to send for, and no way to name the work to the API.
    if (workId == null) return;

    void dispatchFindReaders(button, workId);
  });

  // Premium only, and resolved ONCE here rather than per hover: `viewerIsPremium` reads a
  // module-level constant, and the answer cannot change without a page load. A free member gets no
  // listeners at all, so there is nothing to explain away on the surfaces where their press goes
  // to the marketing page instead.
  if (viewerIsPremium()) {
    // Delegated like the click above, so buttons that mount later — an appended profile card, a
    // re-sorted analytics row — need no rebinding.
    document.addEventListener('mouseover', (event) => {
      const button = (event.target as HTMLElement | null)?.closest<HTMLElement>(
        BUTTON_SELECTOR,
      );
      if (button) showFindReadersTooltip(button);
    });

    // The icon and the label are children of the button, so a pointer crossing between them fires
    // mouseout. Only a move that actually leaves the button should dismiss it.
    document.addEventListener('mouseout', (event) => {
      const button = (event.target as HTMLElement | null)?.closest<HTMLElement>(
        BUTTON_SELECTOR,
      );
      if (!button) return;

      const to = (event as MouseEvent).relatedTarget as Node | null;
      if (to != null && button.contains(to)) return;

      hideFindReadersTooltip();
    });

    // Keyboard parity: the tooltip is the only place the button says what it will do, so it must
    // not be pointer-only. focusin/focusout rather than focus/blur because those do not bubble.
    document.addEventListener('focusin', (event) => {
      const button = (event.target as HTMLElement | null)?.closest<HTMLElement>(
        BUTTON_SELECTOR,
      );
      if (button) showFindReadersTooltip(button);
    });
    document.addEventListener('focusout', hideFindReadersTooltip);

    // A tooltip anchored to viewport coordinates would drift away from its button otherwise, and
    // Escape is what a keyboard user expects to dismiss it.
    window.addEventListener('scroll', hideFindReadersTooltip, true);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') hideFindReadersTooltip();
    });
  }

  window.addEventListener('pageshow', reloadIfRestoredMidDispatch);

  document.addEventListener(SHOWN_EVENT, (event) => {
    const { el } = (event as CustomEvent<{ el: HTMLElement }>).detail ?? {};
    if (el) watchCta(el);
  });

  // Cards present at load may have revealed before this bundle evaluated, in which case their
  // find-readers:shown already fired and was missed. Observing them now costs nothing while they
  // are still hidden: IntersectionObserver reports no intersection for a display:none element
  // and fires when it is revealed, which is exactly when the impression should count.
  document.querySelectorAll<HTMLElement>(CTA_SELECTOR).forEach(watchCta);
};

export default setUpFindReaders;
